if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.disabled || this.submitButton.getAttribute('aria-disabled') === 'true') return;
        // Resolve at click time: section reloads may have replaced the drawer.
        this.cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
        if (this.cart && typeof this.cart.getSectionsToRender !== 'function') {
          this.handleErrorMessage('The cart is still loading. Please try again in a moment.');
          return;
        }

        this.handleErrorMessage();
        this.addSucceeded = false;
        this.error = false;

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then(async (response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              // An API rejection can be a quantity limit, not permanent sold-out status.
              // Keep the original label so the buyer can correct the quantity and retry.
              return;
            }
            this.addSucceeded = true;
            if (!this.cart) {
              window.location.assign(routes.cart_url);
              return;
            }
            const requiredSections = this.cart.getSectionsToRender().map((section) => section.id);
            if (requiredSections.some((id) => !response.sections?.[id])) {
              const url = new URL(window.location.href);
              url.searchParams.set('sections', requiredSections.join(','));
              const refresh = await fetch(url.toString(), { cache: 'no-store' });
              if (!refresh.ok) throw new Error('Cart refresh failed');
              response.sections = await refresh.json();
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;

            // Render server sections; add.js is not a full cart total.

            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.renderCart(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.renderCart(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
            this.handleErrorMessage(this.addSucceeded
              ? 'Your item was added, but the cart could not be displayed. Please open the cart icon to check before adding again.'
              : 'Unable to add this item. Please try again.');
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.addSucceeded && this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      renderCart(cartData) {
        const drawer = document.querySelector('cart-drawer');
        if (!drawer) {
          this.cart.renderContents(cartData);
          return;
        }

        const parser = new DOMParser();
        const drawerDocument = parser.parseFromString(cartData.sections['cart-drawer'] || '', 'text/html');
        const bubbleDocument = parser.parseFromString(cartData.sections['cart-icon-bubble'] || '', 'text/html');
        const nextDrawer = drawerDocument.querySelector('#CartDrawer');
        const nextBubble = bubbleDocument.querySelector('.shopify-section');
        const currentDrawer = drawer.querySelector('#CartDrawer');
        const cartLink = document.getElementById('cart-icon-bubble');
        if (!nextDrawer || !currentDrawer || !nextBubble || typeof drawer.open !== 'function') {
          throw new Error('Cart sections are unavailable');
        }

        // Keep the custom element and header link, including their listeners.
        if (cartLink) cartLink.innerHTML = nextBubble.innerHTML;
        currentDrawer.innerHTML = nextDrawer.innerHTML;
        drawer.classList.remove('is-empty');
        drawer.querySelector('#CartDrawer-Overlay')?.addEventListener('click', () => drawer.close());
        drawer.setActiveElement(this.submitButton);
        drawer.open(this.submitButton);
        this.cart = drawer;
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
